import React, { useState, useEffect, useContext } from 'react';
import { AuthContext } from '../../contexts/AuthContext';
import TimeSlotGrid from './TimeSlotGrid';
import BoardCard from './BoardCard';
import LoadingSpinner from '../common/LoadingSpinner';
import Button from '../common/Button';
import Modal from '../common/Modal';

const BoardView = ({ boardId }) => {
  const { user } = useContext(AuthContext);
  const [board, setBoard] = useState(null);
  const [timeSlots, setTimeSlots] = useState([]);
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showReservationModal, setShowReservationModal] = useState(false);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [reservationNotes, setReservationNotes] = useState('');
  const [isReserving, setIsReserving] = useState(false);
  const [dateBlockedInfo, setDateBlockedInfo] = useState(null);
  const [allBlockedDates, setAllBlockedDates] = useState([]);

  useEffect(() => {
    if (boardId) {
      loadBoardData();
    }
  }, [boardId]);

  useEffect(() => {
    if (board && selectedDate) {
      checkBlockedDate();
      loadTimeSlots();
    }
  }, [board, selectedDate]);

  useEffect(() => {
    if (board) {
      loadAllBlockedDates();
    }
  }, [board]);

  const loadBoardData = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:3001'}/api/boards/${boardId}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to load board data');
      }

      const boardData = await response.json();
      setBoard(boardData);
    } catch (error) {
      console.error('Error loading board:', error);
      setError(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const loadAllBlockedDates = async () => {
    if (!board) return;

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL || 'http://localhost:3001'}/api/blocked-dates/gym/${board.gym_id}`
      );

      if (response.ok) {
        const data = await response.json();
        setAllBlockedDates(data.map(d => d.blocked_date));
      }
    } catch (error) {
      console.error('Error loading blocked dates:', error);
      setAllBlockedDates([]);
    }
  };

  const checkBlockedDate = async () => {
    if (!board || !selectedDate) return;

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL || 'http://localhost:3001'}/api/blocked-dates/gym/${board.gym_id}/check/${selectedDate}`
      );

      if (response.ok) {
        const data = await response.json();
        setDateBlockedInfo(data.isBlocked ? data : null);
      }
    } catch (error) {
      console.error('Error checking blocked date:', error);
      setDateBlockedInfo(null);
    }
  };

  const loadTimeSlots = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:3001'}/api/reservations/slots/${boardId}?date=${selectedDate}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to load time slots');
      }

      const slotsData = await response.json();
      setTimeSlots(slotsData);
    } catch (error) {
      console.error('Error loading time slots:', error);
      // Don't set error here as it might just be no slots available
    }
  };

  const handleSlotSelect = (slot) => {
    if (slot.reservation_id || slot.is_blocked) {
      return; // Can't select occupied or blocked slots
    }
    
    setSelectedSlot(slot);
    setShowReservationModal(true);
  };

  const handleReservation = async () => {
    if (!selectedSlot) return;

    setIsReserving(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:3001'}/api/reservations`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          timeSlotId: selectedSlot.id,
          notes: reservationNotes
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to create reservation');
      }

      // Refresh time slots and close modal
      await loadTimeSlots();
      setShowReservationModal(false);
      setSelectedSlot(null);
      setReservationNotes('');
    } catch (error) {
      console.error('Error creating reservation:', error);
      alert(error.message);
    } finally {
      setIsReserving(false);
    }
  };

  const handleDateChange = (newDate) => {
    setSelectedDate(newDate);
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner size="large" text="Loading board..." />
      </div>
    );
  }

  if (error || !board) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">
            {error || 'Board not found'}
          </h2>
          <Button onClick={() => window.history.back()}>
            Go back
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="py-6">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="mb-6">
            <Button 
              onClick={() => window.history.back()}
              variant="outline"
              size="small"
              className="mb-4"
            >
              ← Back
            </Button>
            <h1 className="text-3xl font-bold text-gray-900">
              {board.name}
            </h1>
            <p className="mt-1 text-sm text-gray-600">
              {board.type} Board • {board.location}
            </p>
          </div>

          {/* Board Info Card */}
          <div className="mb-8">
            <BoardCard board={board} showReserveButton={false} showViewButton={false} />
          </div>

          {/* Date Picker */}
          <div className="mb-6">
            <label htmlFor="date" className="block text-sm font-medium text-gray-700 mb-2">
              Select Date
            </label>
            <input
              type="date"
              id="date"
              value={selectedDate}
              onChange={(e) => handleDateChange(e.target.value)}
              min={new Date().toISOString().split('T')[0]}
              className="block w-full sm:w-auto px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
            />

            {/* Show upcoming blocked dates */}
            {allBlockedDates.length > 0 && (() => {
              const today = new Date();
              today.setHours(0, 0, 0, 0);
              const upcomingBlocked = allBlockedDates
                .filter(dateStr => {
                  const date = new Date(dateStr + 'T00:00:00');
                  return date >= today;
                })
                .slice(0, 5); // Show next 5 blocked dates

              if (upcomingBlocked.length === 0) return null;

              return (
                <div className="mt-2 p-2 bg-yellow-50 border border-yellow-200 rounded-md">
                  <p className="text-xs font-medium text-yellow-800 mb-1">
                    Upcoming Blocked Dates:
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {upcomingBlocked.map(dateStr => (
                      <span
                        key={dateStr}
                        className="inline-flex items-center px-2 py-1 rounded text-xs bg-yellow-100 text-yellow-800"
                      >
                        {new Date(dateStr + 'T00:00:00').toLocaleDateString('en-US', {
                          month: 'short',
                          day: 'numeric',
                          year: 'numeric'
                        })}
                      </span>
                    ))}
                  </div>
                </div>
              );
            })()}
          </div>

          {/* Blocked Date Warning */}
          {dateBlockedInfo && (
            <div className="mb-6 bg-red-50 border border-red-200 rounded-md p-4">
              <div className="flex">
                <svg className="h-5 w-5 text-red-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-red-800">Reservations Unavailable</h3>
                  <p className="mt-2 text-sm text-red-700">
                    The gym is closed on this date. Reason: {dateBlockedInfo.reason}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Time Slots Grid */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">
              Available Time Slots - {new Date(selectedDate + 'T12:00:00').toLocaleDateString()}
            </h2>
            {dateBlockedInfo ? (
              <div className="text-center py-12 text-gray-500">
                <svg className="mx-auto h-12 w-12 text-gray-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
                </svg>
                <p className="mt-2">No time slots available for this date</p>
              </div>
            ) : (
              <TimeSlotGrid
                timeSlots={timeSlots}
                onSlotSelect={handleSlotSelect}
                selectedDate={selectedDate}
              />
            )}
          </div>
        </div>
      </div>

      {/* Reservation Modal */}
      <Modal
        isOpen={showReservationModal}
        onClose={() => setShowReservationModal(false)}
        title="Confirm Reservation"
        footer={
          <div className="space-x-3">
            <Button
              onClick={() => setShowReservationModal(false)}
              variant="outline"
            >
              Cancel
            </Button>
            <Button
              onClick={handleReservation}
              loading={isReserving}
              disabled={isReserving}
            >
              Reserve Slot
            </Button>
          </div>
        }
      >
        {selectedSlot && (
          <div className="space-y-4">
            <div>
              <h3 className="text-lg font-medium text-gray-900">
                {board.name}
              </h3>
              <p className="text-sm text-gray-600">
                {new Date(selectedSlot.start_time).toLocaleString()} - {new Date(selectedSlot.end_time).toLocaleString()}
              </p>
            </div>
            
            <div>
              <label htmlFor="notes" className="block text-sm font-medium text-gray-700 mb-2">
                Notes (optional)
              </label>
              <textarea
                id="notes"
                rows={3}
                value={reservationNotes}
                onChange={(e) => setReservationNotes(e.target.value)}
                className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                placeholder="Add any notes about your training session..."
                maxLength={500}
              />
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default BoardView;